import { useNavigate, useParams } from "react-router-dom";
import useFetch from "@/lib/useFetch";
import { Loading, ErrorState, Avatar } from "@/components/common";
import Voltar from "@/components/Voltar";
import { useAuth } from "@/context/AuthContext";
import CobrarWhatsApp from "@/components/CobrarWhatsApp";
import { brl, pct, num } from "@/lib/format";
import { Phone, Mail, Users, Briefcase, Trophy, DollarSign, TrendingUp, GitBranch, ListTodo, MapPin, ChevronRight } from "@/components/icons";

function StatCard({ icon: Icon, label, value, accent, onClick, testId }) {
  const clickable = typeof onClick === "function";
  const cls = [
    "text-left bg-slate-50 rounded-xl p-4 border border-transparent transition-all",
    clickable ? "hover:border-brand/30 hover:bg-brand-soft/40 hover:shadow-sm cursor-pointer" : "cursor-default",
  ].join(" ");
  return (
    <button type="button" onClick={onClick} disabled={!clickable} className={cls} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent || "bg-brand-soft text-brand-hover"}`}>
          <Icon className="w-4 h-4" />
        </div>
        {clickable && <ChevronRight className="w-4 h-4 text-slate-400" />}
      </div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-3">{label}</div>
      <div className="text-lg font-semibold text-navy mt-0.5 truncate">{value}</div>
    </button>
  );
}

export default function MembroDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: m, loading, error, reload } = useFetch(`/equipe/${id}`, [id]);
  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} message="Membro não encontrado ou fora do seu escopo." />;

  const isSelf = user?.id === m.id;
  const hasOwnGoal = m.goal != null && Number(m.goal) > 0;
  const vendasDiretas = Number(m.vendas_diretas_sankhya ?? m.invoiced_sales ?? 0);

  const goClientes = () => navigate(`/clientes?scope_id=${id}`);
  const goPipeline = (extra = "") => navigate(`/pipeline?scope_id=${id}${extra}`);
  const goAtividades = () => navigate(`/atividades?tab=equipe&owner=${encodeURIComponent(m.nome || "")}`);
  // "Faturamento" (Sankhya) abre a lista de clientes do vendedor na tela Faturamento.
  const goFaturamentoClientes = () => navigate(`/faturamento?user_id=${id}`);

  return (
    <div>
      <Voltar fallback="/equipe" label="Equipe" className="mb-4" />

      <div className="bg-card rounded-2xl border border-black/5 p-5 sm:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Avatar name={m.nome} src={m.avatar} size={64} />
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-semibold text-navy truncate" data-testid="membro-titulo">
              {isSelf ? "Minha carteira" : m.nome}
            </h1>
            <p className="text-sm text-muted-foreground">{m.cargo}{isSelf ? ` · ${m.nome}` : ""}</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:gap-4 gap-1 mt-2 text-sm text-slate-600">
              {m.telefone && (
                <a href={`tel:${m.telefone}`} className="flex items-center gap-1.5 hover:text-brand" data-testid="membro-telefone">
                  <Phone className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{m.telefone}</span>
                </a>
              )}
              {m.email && (
                <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 hover:text-brand min-w-0" data-testid="membro-email">
                  <Mail className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{m.email}</span>
                </a>
              )}
            </div>
          </div>
          <div className="shrink-0">
            <CobrarWhatsApp
              vendedor={{ id: m.id, name: m.nome, phone: m.telefone }}
              origem="equipe"
              testId={`membro-${m.id}`}
            />
          </div>
        </div>

        {hasOwnGoal && (
          <div className="mt-5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground truncate">
                {m.sem_vinculo_sankhya ? "Sem vínculo com o Sankhya" : `${brl(m.invoiced_sales)} / ${brl(m.goal)}`}
              </span>
              <span className="font-semibold text-brand shrink-0 ml-3">{pct(m.achievement)}</span>
            </div>
            <div className="h-2.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-brand rounded-full transition-all duration-700" style={{ width: `${Math.min(100, m.achievement || 0)}%` }} />
            </div>
            {m.atualizado_em_sankhya ? (
              <div className="text-[11px] text-muted-foreground mt-1" data-testid="membro-sankhya-footer">
                Faturamento: Sankhya · atualizado às {new Date(m.atualizado_em_sankhya).toLocaleTimeString("pt-BR", {hour:"2-digit", minute:"2-digit"})}
              </div>
            ) : null}
          </div>
        )}

        {!hasOwnGoal && isSelf && !m.sem_vinculo_sankhya && (
          <div className="mt-5 text-sm" data-testid="membro-vendas-diretas">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Vendas diretas</span>
            <div className="text-navy font-semibold text-base tabular-nums">{brl(vendasDiretas)}</div>
            {m.atualizado_em_sankhya ? (
              <div className="text-[11px] text-muted-foreground mt-1">
                Faturamento: Sankhya · atualizado às {new Date(m.atualizado_em_sankhya).toLocaleTimeString("pt-BR", {hour:"2-digit", minute:"2-digit"})}
              </div>
            ) : null}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <StatCard icon={Users} label="Clientes" value={num(m.clientes_count)} onClick={goClientes} testId="membro-card-clientes" />
          <StatCard icon={Briefcase} label="Oportunidades" value={num(m.deals_count)} onClick={() => goPipeline()} testId="membro-card-oportunidades" />
          <StatCard icon={Trophy} label="Ganhas" value={num(m.won_count)} accent="bg-ok-soft text-ok" onClick={() => goPipeline("&stage=Fechado")} testId="membro-card-ganhas" />
          <StatCard
            icon={DollarSign}
            label="Faturamento"
            value={m.sem_vinculo_sankhya ? "Sem vínculo" : brl(m.invoiced_sales)}
            accent="bg-ok-soft text-ok"
            onClick={m.sem_vinculo_sankhya ? undefined : goFaturamentoClientes}
            testId="membro-card-faturamento"
          />
          <StatCard icon={Trophy} label="Ganhos no CRM" value={brl(m.ganhos_crm || 0)} onClick={() => goPipeline("&stage=Fechado")} testId="membro-card-ganhos-crm" />
          <StatCard icon={TrendingUp} label="Conversão" value={pct(m.conversion, 1)} onClick={() => goPipeline()} testId="membro-card-conversao" />
          <StatCard icon={GitBranch} label="Pipeline" value={brl(m.pipeline_value)} onClick={() => goPipeline("&apenas_ativas=1")} testId="membro-card-pipeline" />
          <StatCard icon={ListTodo} label="Ativ. pendentes" value={num(m.atividades_pendentes)} accent="bg-warn-soft text-warn" onClick={goAtividades} testId="membro-card-atividades" />
          <StatCard icon={MapPin} label="Visitas" value={num(m.visitas_agendadas)} onClick={goAtividades} testId="membro-card-visitas" />
        </div>
      </div>

      {!isSelf && m.subordinates?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Equipe vinculada</h3>
          <div className="space-y-2">
            {m.subordinates.map((s) => (
              <div key={s.id} onClick={() => navigate(`/membro/${s.id}`)} data-testid={`sub-${s.id}`}
                className="bg-card rounded-xl border border-black/5 p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors">
                <Avatar name={s.nome} src={s.avatar} size={38} />
                <div className="flex-1 min-w-0"><div className="font-medium text-navy truncate">{s.nome}</div><div className="text-xs text-muted-foreground">{s.cargo} · Meta {brl(s.goal || 0)}</div></div>
                <div className="text-right shrink-0"><div className="font-semibold text-navy text-sm tabular-nums">{brl(s.invoiced_sales || 0)}</div><div className="text-xs text-muted-foreground tabular-nums">{pct(s.achievement)}</div></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
